import React from 'react';
import Joi from 'joi';
import getConfig from 'next/config';
import _ from 'underscore';
import {
	api as apiRequest,
	batchApi as batchApiRequest
} from '@culture/common-helpers/request';
import { addCleanCache } from '@culture/common-helpers/params';

import { addQuery, filterQuery } from '@culture/common-helpers/url';

import { publicationTypesHash } from '@culture/utils/helpers/publications';
import Template from 'pageTemplates/articles';

const ThemesCatalogPage = (props) => <Template {...props} />;

const { serverRuntimeConfig } = getConfig();

const rootRubricId = serverRuntimeConfig.entities?.lecturesThemesRubric;

const getRubricPage = (rubricPath, cleanCache) =>
	apiRequest(
		'rubricPages/page',
		addCleanCache(
			{
				rubricPath,
				blockFields: {
					persons: ['_id'].join(),
					incutSimplePromoBlock: ['_id'].join(),
					themes: ['_id'].join(),
					incutGridPromoBlock: [
						'title',
						'type',
						'format',
						'url',
						'grid',
						'text'
					].join(),
					articles: ['_id'].join()
				},
				completeBlocksParams: [
					{
						blockName: 'articles',
						blockParams: {
							limit: 0
						}
					},
					{
						blockName: 'movies',
						blockParams: {
							limit: 0
						}
					},
					{
						blockName: 'persons',
						blockParams: {
							limit: 0
						}
					},
					{
						blockName: 'themes',
						blockParams: {
							limit: 0
						}
					}
				]
			},
			cleanCache
		)
	);

const getRootRubric = (cleanCache) =>
	apiRequest(
		`rubrics/${rootRubricId}`,
		addCleanCache(
			{
				fields: ['name', 'path', 'title'].join()
			},
			cleanCache
		)
	);

const getPublications = ({ urlParams, filtersQueryParams, rubricId }) => {
	const params = filterQuery({
		fields: ['title', 'thumbnailFile', 'shortText', 'name', 'type'].join(),
		..._(filtersQueryParams).pick('query', 'sort', 'page'),
		cached: true,
		statuses: ['published'],
		types: [publicationTypesHash.themes],
		rubrics: [rubricId],
		limit: 16
	});

	return apiRequest(
		'publications',
		addCleanCache(params, urlParams.cleanCache)
	);
};

const getMeta = ({ urlParams, rubricPath }) =>
	apiRequest(
		'meta/catalog',
		addCleanCache(
			{
				..._(urlParams).pick('page'),
				rubricPath,
				cached: true
			},
			urlParams.cleanCache
		)
	);

const getBreadcrumbs = ({ cleanCache, rubricPath }) => {
	const params = addCleanCache(
		{
			rubricPath,
			cached: true
		},
		cleanCache
	);

	return apiRequest('breadcrumbs', params);
};

export async function getServerSideProps({ query, resolvedUrl }) {
	const defaultSort = '-publishDate';
	const validationSchema = Joi.object().keys({
		cleanCache: Joi.boolean().failover(false),
		page: Joi.number().default(1).min(1),
		sort: Joi.string()
			.valid('views', '-views', 'publishDate', '-publishDate')
			.failover(defaultSort)
			.default(defaultSort)
	});

	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const { cleanCache } = params;

	const rootRubric = await getRootRubric(cleanCache);

	const {
		name: rootRubricName,
		path: rootRubricPath,
		_id: rubricId
	} = rootRubric;

	const filtersQueryParams = filterQuery({
		..._(params).pick('sort', 'page', 'cleanCache')
	});

	const requests = [
		getPublications({
			urlParams: params,
			filtersQueryParams,
			rubricId
		}),
		getMeta({ urlParams: params, rubricPath: rootRubricPath }),
		getBreadcrumbs({ cleanCache, rubricPath: rootRubricPath }),
		getRubricPage(rootRubricPath, cleanCache)
	];

	const [publications, meta, breadcrumbs, rubricPage] = await batchApiRequest(
		requests
	);

	const {
		blocks: { incutGridPromoBlock }
	} = rubricPage;

	if (
		publications.pagination.total &&
		publications.pagination.total < params.page
	) {
		return {
			redirect: {
				destination: addQuery(`${serverRuntimeConfig.baseUrl}${resolvedUrl}`, {
					page: publications.pagination.total
				}),
				permanent: false
			}
		};
	}

	return {
		props: {
			publications,
			meta,
			rootRubricPath,
			rubricName: rootRubricName,
			breadcrumbs,
			urlParams: filtersQueryParams,
			incutGridPromoBlock
		}
	};
}

export default ThemesCatalogPage;
